import { TitleCasePipe } from '@angular/common';
import {
  ColDef,
  SideBarDef,
  ToolPanelDef,
  ValueFormatterParams,
} from 'ag-grid-community';
import { formatDate, formatDateToYYYYMMDD } from './date.utils';

/** Common default column config */
export const defaultColDef = {
  sortable: true,
  filter: true,
  floatingFilter: true,
  lockPinned: true,
} satisfies Pick<
  ColDef<any>,
  'sortable' | 'filter' | 'floatingFilter' | 'lockPinned'
>;

/** A named constant for the en-dash character (–), used for placeholder text in the grid. */
export const EN_DASH = '\u2013';

/** Common formatters */
export const upperCaseFormatter = (params: ValueFormatterParams) =>
  params.value ? params.value.toString().toUpperCase() : '';

export const mixedValueFormatter = (field: string) => {
  return (params: any): string => {
    const value = params.value;

    if (typeof value === 'boolean') {
      return value ? 'YES' : 'NO';
    }

    if (!value) return '';

    if (typeof value === 'string') {
      return value.toString().toUpperCase();
    }

    return value[field] ? value[field].toString().toUpperCase() : '';
  };
};

export const displayWith = (value: any, field: string) => {
  if (!value) return '';

  if (typeof value === 'string') {
    return value;
  }

  return value[field] ? value[field] : '';
};

export const titleCaseFormatter =
  (titleCasePipe: TitleCasePipe) => (params: ValueFormatterParams) =>
    titleCasePipe.transform(params.value);

export const dateFormatterYYYYMMDD = (
  params: ValueFormatterParams<any, string>
) => formatDateToYYYYMMDD(params.value);

export const dateFormatter = (params: ValueFormatterParams<any, string>) =>
  formatDate(params.value);

/** Common checkbox column */
export const checkboxColumn: ColDef<any> = {
  headerName: '',
  checkboxSelection: true,
  headerCheckboxSelection: false,
  maxWidth: 35,
  filter: false,
  floatingFilter: false,
};

export const baseCheckboxCol = {
  headerName: '',
  maxWidth: 35,
  filter: false,
  floatingFilter: false,
  suppressColumnsToolPanel: true,
  suppressMovable: true,
  pinned: 'left',
  lockPosition: 'left',
} satisfies Pick<
  ColDef<any>,
  | 'headerName'
  | 'maxWidth'
  | 'filter'
  | 'floatingFilter'
  | 'suppressColumnsToolPanel'
  | 'suppressMovable'
  | 'pinned'
  | 'lockPosition'
>;

/** Row drag handle column */
export const baseRowDragCol = {
  ...baseCheckboxCol,
  colId: 'rowDrag',
  rowDrag: true,
  sortable: false,
  resizable: false,
  flex: 0,
} satisfies ColDef<unknown>;

/** Row sequence column */
export const baseRowSequenceCol = {
  ...baseCheckboxCol,
  editable: false,
  resizable: false,
  flex: 0,
} satisfies ColDef<unknown>;

/** Base action column config - extend with cellRenderer */
export const baseActionCol = {
  headerName: '',
  filter: false,
  floatingFilter: false,
  editable: false,
  suppressColumnsToolPanel: true,
  flex: 1,
} satisfies Pick<
  ColDef<any>,
  | 'headerName'
  | 'filter'
  | 'floatingFilter'
  | 'editable'
  | 'flex'
  | 'suppressColumnsToolPanel'
>;

/** Empty column helper */
export const emptyColumn: ColDef<any> = {
  headerName: '',
  filter: false,
  floatingFilter: false,
  editable: false,
  flex: 1,
};

/**
 * Utility function to create a standard column with standard sorting/filtering
 */
export function createColumn<T>(config: Partial<ColDef<T>>): ColDef<T> {
  return {
    ...defaultColDef,
    ...config,
  } as ColDef<T>;
}

/**
 * Disables column and floating filters for all supplied columns.
 */
export function withoutColumnFilters<T>(colDefs: ColDef<T>[]): ColDef<T>[] {
  return colDefs.map((colDef) => ({
    ...colDef,
    filter: false,
    floatingFilter: false,
  }));
}

export const columnsToolPanel: ToolPanelDef = {
  id: 'columns',
  labelDefault: 'Popup',
  labelKey: 'columns',
  iconKey: 'columnsToolPanel',
  toolPanel: 'agColumnsToolPanel',
  toolPanelParams: {
    suppressRowGroups: true,
    suppressValues: true,
    suppressPivotMode: true,
  },
};

export const FILLER_COLUMN: ColDef = {
  colId: 'filler',
  headerName: '',
  flex: 1,
  minWidth: 0,
  suppressColumnsToolPanel: true,
  suppressMovable: true,
  sortable: false,
  filter: false,
  resizable: false,
  suppressAutoSize: true,
};

export const sideBarDef: SideBarDef = {
  hideButtons: true,
  hiddenByDefault: true,
};

export const COLUMN_WIDTHS = {
  protocolCarrier: 100,
  protocolGroupName: 150,
  protocolGroupDescription: 250,
  applicableForeignProtocolCarrier: 90,
  splcName: 300,
  routingType: 170,
  protocolRoute: 300,
  pubFormatAndType: 100,
  publicationFormat: 150,
  pubNumber: 170,
  pubAppendixItem: 140,
  businessUnitType: 200,
  com3: 230,

  stcc: 230,
  originDestSPLC: 170,
  originDestProvState: 100,
  originDestCarrier: 120,
  customerName: 140,
  customerRole: 150,
  dimensionalClass: 240,
  waybillJunction: 100,

  date: 110,
  count: 80,
  user: 100,
  dateTime: 150,

  notes: 440,
  protocolVersionId: 140,
  actionMenu: 78,
  stationProvince: 150,
  StationSCAC: 100,
  publicationCommentsEmail: 100,
  publicationStatus: 120,
  rd: 70,
  currency: 100,
  uom: 130,
  rateQualifiers: 130,
  closed: 32,
  rowDrag: 30,
  rowSequence: 40,
};
